import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import PageVisibility from 'react-page-visibility';
import { isNil, isEmpty, debounce } from 'lodash';
import FlipMove from 'react-flip-move';
import Media from 'components/Media';
import Bingo from 'modules/Bingo';
import { easing } from 'lib/constants';
import Ticket from './Ticket';
import TicketBuyPanel from './TicketBuyPanel';
import './myTickets.css';

const ANIMATION_INITIAL_DURATION = 450;
const LAZY_RENDER_MS = 140;
const padding = () => window.innerWidth * 0.01; // 1vw
// Last ticket is replaced with "X more tickets" message
const IN_PLAY_LIMIT = 13;

const getColumnCount = (ticket, currentRect) => {
  const currentTop = currentRect.top;
  let next = ticket.nextElementSibling;
  let nextRect = next && next.getBoundingClientRect();
  let count = 1;

  while (nextRect && nextRect.top - currentTop < 4 && nextRect.top - currentTop > -4) {
    next = next.nextElementSibling;
    nextRect = next && next.getBoundingClientRect();
    count++;
  }

  return count;
};

// const getColumnCount = (containerWidth) => {
//   if (containerWidth < 451) return 1;
//   if (containerWidth < 691) return 2;
//   if (containerWidth < 963) return 3;
//   if (containerWidth < 1261) return 4;
//   if (containerWidth < 1588) return 5;
//   return 6;
// };

// TODO: The previous version has debouncing more suitable to desktop (non-batched). Split them.
// WARNING: This will fail if dyynamically changing the height
export class _TicketList extends Component {
  handleScroll = debounce(() => {
    const { refs } = this.props;
    const { documentHidden } = this.state;
    const first = refs && refs[0];

    if (!first || documentHidden) return;

    if (this.first !== first) {
      this.first = first;
      this.firstTicket = document.getElementById(first);
    }

    const ticketCount = refs.length;
    const containerRect = this.containerRef.getBoundingClientRect();
    const ticketRect = this.firstTicket.getBoundingClientRect();

    if (!this.containerWidth || !this.columnCount || this.containerWidth !== containerRect.width) {
      this.containerWidth = containerRect.width;
      this.columnCount = getColumnCount(this.firstTicket, ticketRect);
    }

    // Negative == hidden. Multiply.
    const yTop = ticketRect.bottom - containerRect.top;
    const yBottom = ticketRect.top - containerRect.bottom;
    const visibleTopRow = Math.abs(Math.floor(-yTop / (this.padding + ticketRect.height)));
    const visibleBottomRow = Math.trunc(-yBottom / (this.padding + ticketRect.height));
    let firstVisibleIndex = (visibleTopRow + 1) * this.columnCount;
    const lastVisibleIndex = (visibleBottomRow + 1) * this.columnCount;

    if (yTop > 0) firstVisibleIndex = 0;

    const result = {};

    for (let i = firstVisibleIndex; i < Math.min(lastVisibleIndex, ticketCount); i++) {
      result[refs[i]] = true;
    }

    this.setState({ inView: result });
  }, LAZY_RENDER_MS);

  state = { documentHidden: false, inView: {} };

  componentDidMount() {
    const { refs } = this.props;
    if (!isEmpty(refs)) {
      this.handleScroll();
    }
    this.windowHeight = window.innerHeight;
    this.padding = padding(); // Because the padding (grid-gap) is in VW units
    window.addEventListener('resize', this.handleWinResize);
  }

  componentDidUpdate({ refs: prevRefs, isActive: prevIsActive }) {
    const { refs, isActive } = this.props;
    if ((refs !== prevRefs && refs) || (isActive && !prevIsActive)) {
      this.handleScroll();
    }
  }

  componentWillUnmount() {
    this.handleScroll.cancel();
    window.removeEventListener('resize', this.handleWinResize);
  }

  setContainerRef = (_) => {
    this.containerRef = _;
  };

  // TODO: Move to redux (top-level)
  handleWinVisibility = (isVisible) => {
    this.setState({ documentHidden: !isVisible });
    if (isVisible && this.props.refs) {
      this.firstTicket = document.getElementById(this.props.refs[0]);
      this.handleScroll();
    }
  };

  handleWinResize = (event) => {
    const currentHeight = event.currentTarget.innerHeight;
    this.padding = padding();
    if (this.windowHeight !== currentHeight) {
      this.windowHeight = currentHeight;
      this.handleScroll();
    }
  };

  render() {
    const {
      refName,
      refs,
      type,
      entities,
      selectedRefs,
      ticketSelect,
      ticketPrice,
      priceLabel,
      dropped,
      inPlay,
      inProgress,
      isActive,
      maxToGo,
      pictureNumbersImageUrl
    } = this.props;
    const { documentHidden, inView } = this.state;

    const freeRoom = ticketPrice <= 0;
    const computedSelectedRefs = freeRoom ? refs : selectedRefs;
    const totalPrice = computedSelectedRefs && computedSelectedRefs.length * ticketPrice;
    const showBuy = !isNil(ticketPrice);
    const refsToRender = inPlay && refs ? refs.slice(0, IN_PLAY_LIMIT) : refs;
    const tickets =
      refsToRender && refsToRender.length > 0 && !documentHidden && !isEmpty(entities)
        ? refsToRender.map((ticketRef, index) => {
            const selected = computedSelectedRefs.includes(ticketRef);
            return (
              <Ticket
                key={ticketRef}
                index={index}
                type={type}
                pictureNumbersImageUrl={pictureNumbersImageUrl}
                ticketSelect={ticketSelect}
                selected={selected}
                priceLabel={priceLabel}
                dropped={dropped}
                maxToGo={maxToGo}
                rows={entities[ticketRef].rows}
                purchased={entities[ticketRef].purchased}
                purchasedAt={entities[ticketRef].purchasedAt}
                refName={ticketRef}
                inView={inPlay || inView[ticketRef]}
                xMore={
                  (inPlay &&
                    index === refsToRender.length - 1 &&
                    refs.length - refsToRender.length) ||
                  0
                }
              />
            );
          })
        : null;

    const list = inPlay ? (
      <FlipMove
        delay={inPlay ? 200 : 0}
        duration={ANIMATION_INITIAL_DURATION}
        easing={easing}
        staggerDelayBy={inPlay ? 0 : 80}
        className="itemList"
        disableAllAnimations={documentHidden || !isActive}
        enterAnimation={false}
        leaveAnimation={false}
        appearAnimation={false}
      >
        {tickets}
      </FlipMove>
    ) : (
      <div className="itemList">{tickets}</div>
    );

    // TODO: const staggerMS = refs && refs.length < XX ? 40 : 0;
    return (
      <div className="buyView-wrapper">
        <PageVisibility onChange={this.handleWinVisibility}>
          <div
            className="buyView"
            key={documentHidden.toString()}
            ref={this.setContainerRef}
            onScroll={this.handleScroll}
          >
            {!documentHidden ? list : null}
          </div>
        </PageVisibility>
        <div>
          {showBuy && (
            <Media query="(max-width: 768px)">
              {(matches) => (
                <TicketBuyPanel
                  refName={refName}
                  className="bottom affixed"
                  ticketCount={computedSelectedRefs && computedSelectedRefs.length}
                  totalPrice={totalPrice}
                  free={freeRoom}
                  whatToBuy={computedSelectedRefs}
                  autoHide={matches}
                  max={refs.length}
                  switchTab={!inPlay && !inProgress}
                />
              )}
            </Media>
          )}
        </div>
      </div>
    );
  }
}

_TicketList.propTypes = {
  type: PropTypes.oneOf(['30', '75', '90']),
  refName: PropTypes.string,
  priceLabel: PropTypes.string,
  refs: PropTypes.arrayOf(PropTypes.string),
  entities: PropTypes.object,
  selectedRefs: PropTypes.arrayOf(PropTypes.string),
  ticketSelect: PropTypes.func,
  inPlay: PropTypes.bool,
  inProgress: PropTypes.bool,
  isActive: PropTypes.bool,
  dropped: PropTypes.arrayOf(PropTypes.number),
  ticketPrice: PropTypes.number,
  maxToGo: PropTypes.number,
  pictureNumbersImageUrl: PropTypes.string
};

_TicketList.defaultProps = {
  refName: '',
  priceLabel: null,
  type: null,
  refs: null,
  entities: null,
  inPlay: false,
  inProgress: false,
  isActive: false,
  ticketSelect: null,
  selectedRefs: [],
  dropped: [],
  ticketPrice: null,
  maxToGo: 25,
  pictureNumbersImageUrl: null
};

const mapStateToProps = (state, { refName }) => {
  const inPlay = Bingo.selectors.getInPlay(state);
  const inProgress = Bingo.selectors.getInProgress(state);

  return {
    type: Bingo.selectors.getGameType(state, refName),
    dropped: refName ? Bingo.selectors.getDropped(state, null, 'current') : undefined,
    inPlay: inPlay,
    inProgress: inProgress,
    maxToGo: Bingo.selectors.getPatternCount(state, refName, 'current'),
    pictureNumbersImageUrl:
      inPlay || inProgress
        ? Bingo.selectors.getPictureNumbersImageCfUrl(state, refName, 'current')
        : Bingo.selectors.getPictureNumbersImageCfUrl(state, refName, 'next')
  };
};

const TicketList = connect(mapStateToProps)(_TicketList);

TicketList.propTypes = {
  refName: PropTypes.string
};

export default TicketList;
